import { Button, Icon, TextButton, Trigger } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Address } from "../../contactUpdateInquiries/Address";
import { useContactUpdateInquiry } from "../../contactUpdateInquiries/ContactUpdateInquiryProvider";
import { AddressFormState } from "../../shared/types/address";
import {
	DrawerFormButtonContainer,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ValueWrapper } from "../../shared/view/ViewComponents";
import { ContactAddressDrawer } from "./ContactAddressDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: address;
`;

const StyledPrimaryLabel = styled(PrimaryLabel)`
	align-self: center;
`;

export const ContactViewAddressSection: FC = () => {
	const { t } = useTranslation();
	const { addresses, setAddresses } = useContactUpdateInquiry();

	const emptyAddress: AddressFormState = useMemo(
		() => ({
			type: "work",
			city: "",
			street: "",
			houseNumber: "",
			zip: "",
			additionalInfo: "",
			country: null,
			isPrimary: false,
		}),
		[],
	);

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Addresses")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{addresses.map((address, index) => (
					<Trigger
						key={`address_${index}`}
						render={(props) => (
							<ContactAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit address")}
								initialValues={address}
								onClosed={() => void 0}
								onSubmit={async ({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									setAddresses((current) =>
										current.map((address, innerIndex) =>
											innerIndex === index
												? {
														...values,
														id: address.id,
														country: country,
													}
												: { ...address, isPrimary: values.isPrimary ? false : address.isPrimary },
										),
									);

									return true;
								}}
							>
								{!address.isPrimary && (
									<DrawerFormButtonContainer>
										<Button
											type="button"
											color="danger"
											onClick={() => {
												setAddresses((current) => current.toSpliced(index, 1));
											}}
										>
											{t("Delete address")}
										</Button>
									</DrawerFormButtonContainer>
								)}
							</ContactAddressDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<Address address={address} />
									{address.isPrimary && <StyledPrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<ContactAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add address")}
								initialValues={emptyAddress}
								onClosed={() => void 0}
								onSubmit={async ({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									setAddresses((current) => [
										...current.map((address) => ({
											...address,
											isPrimary: values.isPrimary ? false : address.isPrimary,
										})),
										{
											...values,
											id: -1,
											country: country,
										},
									]);

									return true;
								}}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add address")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
